import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Loader2, MessageCircle, Globe, BookOpen, ArrowLeft, RefreshCw } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import api from '../api/client'

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'ht', name: 'Haitian Creole', native: 'Kreyòl Ayisyen' },
  { code: 'zh', name: 'Mandarin Chinese', native: '中文' },
]

function TranslatePage() {
  const { documentId } = useParams()
  const navigate = useNavigate()
  const [document, setDocument] = useState(null)
  const [translation, setTranslation] = useState(null)
  const [language, setLanguage] = useState('en')
  const [loading, setLoading] = useState(true)
  const [translating, setTranslating] = useState(false)
  const [error, setError] = useState(null)

  // Load document
  useEffect(() => {
    api.get(`/documents/${documentId}`)
      .then(res => setDocument(res.data))
      .catch(() => setError('Document not found'))
      .finally(() => setLoading(false))
  }, [documentId])

  // Translate when document loads or language changes
  useEffect(() => {
    if (!document || document.status !== 'ready') return
    setTranslating(true)
    setError(null)

    api.post('/translate', { document_id: parseInt(documentId), language_code: language })
      .then(res => setTranslation(res.data))
      .catch(err => setError(err.response?.data?.error || 'Translation failed'))
      .finally(() => setTranslating(false))
  }, [document, language, documentId])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
    )
  }

  if (error && !translation) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-red-600 text-lg mb-4">{error}</p>
        <Link to="/" className="btn-primary">Go Back</Link>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Top Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 rounded-xl hover:bg-gray-100">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold">{document?.filename}</h2>
            <p className="text-sm text-gray-500">
              {document?.page_count} page{document?.page_count !== 1 ? 's' : ''} uploaded
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Language Selector */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2">
            <Globe className="w-5 h-5 text-primary" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-transparent font-medium focus:outline-none cursor-pointer"
            >
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.native} ({lang.name})
                </option>
              ))}
            </select>
          </div>

          {/* Chat Button */}
          <Link
            to={`/chat/${documentId}`}
            className="btn-primary flex items-center gap-2"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="hidden sm:inline">Ask Questions</span>
          </Link>
        </div>
      </div>

      {/* Reading Level Badge */}
      {translation?.reading_level && (
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium bg-primary-light text-primary-dark px-3 py-1 rounded-full">
            Reading Level: Grade {translation.reading_level}
          </span>
          {translation.cached && (
            <span className="text-xs text-gray-400 ml-2">Cached result</span>
          )}
        </div>
      )}

      {/* Translation Content */}
      {translating ? (
        <div className="card flex flex-col items-center justify-center py-16">
          <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
          <p className="text-lg font-medium">Translating your document...</p>
          <p className="text-gray-500 mt-2">
            AI is creating a plain-language explanation
            {language !== 'en' && ` in ${LANGUAGES.find(l => l.code === language)?.name}`}
          </p>
        </div>
      ) : translation ? (
        <div className="card prose prose-lg max-w-none">
          <ReactMarkdown>{translation.plain_text}</ReactMarkdown>
        </div>
      ) : null}

      {error && translation && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}
    </div>
  )
}

export default TranslatePage
